<template>
  <div class="modern-color-theme text-left font-poppins flex flex-col gap-2">
    <VSLabel v-if="props.label" :tooltip="props.labelTooltip" :for="id">{{ props.label }}</VSLabel>
    <button
      :id="id"
      ref="button"
      type="button"
      class="flex items-center w-full rounded-md transition duration-150 ease-in-out focus:outline-none"
      :class="[
        theme.button.base,
        theme.button.text,
        computedButtonClass,
        {
          'ring-1 ring-inset': theme.outline,
          'focus:ring-2 focus:ring-blue-600': theme.focus
        }
      ]"
      :disabled="props.disabled"
      :name="props.name"
      :aria-label="props.alt"
      :aria-describedby="descriptionId"
      @click="menuOpen = menuOpen ? false : (!props.readonly && (!!props.open || lazy || optionsFlattened.length > 0))"
    >
      <div v-if="slots.prefix" class="pr-2">
        <slot name="prefix" />
      </div>
      <slot />
      <div class="ml-auto flex gap-2 items-center pl-2">
        <VSResetButton v-if="props.isAnySelected" v-bind="props" @click="reset" />
        <VIcon
          v-if="!props.readonly"
          :size="theme.icon.size"
          :name="menuOpen ? theme.icon.up : theme.icon.down"
          :color="theme.icon.color"
          :class="{
            'opacity-50': props.disabled
          }"
        />
      </div>
    </button>
    <VTeleport v-if="menuOpen" to="body">
      <ul ref="menu" class="modern-color-theme font-poppins fixed z-[1001] w-full my-1 rounded-md bg-neutral-100 shadow-sm ring-1 ring-inset ring-neutral-250 flex flex-col p-2 overflow-y-auto max-h-72" :style="position">
        <li v-if="props.search || props.open" class="flex gap-1" :class="{ 'mb-1': optionsFlattened.length > 0 }">
          <VTooltip :content="searchConfiguration.placeholder">
            <VInput v-model="search" :placeholder="searchConfiguration.placeholder" class="flex-1" :trim="openConfiguration.trim" type="search" buffered allow-empty focus @keydown.esc="menuOpen = false" @keydown.enter.prevent="add">
              <template #suffix>
                <VTooltip v-if="search && props.open" :content="globalLocalize('component-library.options.add')">
                  <button :disabled="!canAdd" type="button" :aria-label="globalLocalize('component-library.options.add')" class="flex items-center justify-center" @click="add">
                    <VIcon color="neutral" name="Solid/add-box" />
                  </button>
                </VTooltip>
                <VIcon v-else color="neutral" name="Solid/search" />
              </template>
            </VInput>
          </VTooltip>
          <div v-if="searchConfiguration.sortable || props.actions.length > 0" class="flex items-center gap-1">
            <VButton v-if="searchConfiguration.sortable" :alt="sortingDirectionLabel" :title="sortingDirectionLabel" color="secondary" variant="outlined" icon @click="sortingDirectionToggle">
              <VIcon color="neutral" size="sm" :name="sortingDirectionIcon" />
            </VButton>
            <VButton v-for="(action, actionIndex) in props.actions" :key="actionIndex" color="secondary" variant="outlined" icon :disabled="action.disabled" :title="action.label" @click="action.action">
              <VIcon color="neutral" size="sm" :name="action.icon" />
            </VButton>
          </div>
        </li>
        <li v-if="slots.actions">
          <slot name="actions" />
        </li>
        <VLazyDisplayable :collection="optionsFlattened" :size="25" loader-container="li" @exhausted="next">
          <template #loader>
            <div v-if="nextLoading" class="flex items-center justify-center pt-2">
              <VSpinner size="sm" />
            </div>
          </template>
          <template #default="{ item: option }">
            <li :style="{ paddingLeft: `${2 * option._nestingLevel}em` }">
              <template v-if="'type' in option">
                <template v-if="option.type === 'group'">
                  <VTooltip :content="option.labelTooltip ?? option.label">
                    <template v-if="props.multiple">
                      <button
                        type="button"
                        class="px-4 pl-3 w-full text-sm leading-5 rounded-md flex gap-2 items-center whitespace-nowrap cursor-pointer hover:bg-primary-150 text-neutral-850"
                        @click.prevent="toggleItemsInGroup(option)"
                      >
                        <VCheckbox
                          :model-value="countSelectedItemsInGroup(option) > 0"
                          :indeterminate="countSelectedItemsInGroup(option) > 0 && countSelectedItemsInGroup(option) < getItemsInGroup(option).length"
                          class="pointer-events-none -ml-2"
                          :tabindex="-1"
                        />
                        <VIcon v-if="option.icon" dense :name="option.icon" />
                        <div class="py-2 truncate">{{ option.label }}</div>
                      </button>
                    </template>
                    <template v-else>
                      <div class="px-4 pl-3 py-2 w-full text-sm leading-5 rounded-md flex gap-2 items-center whitespace-nowrap">
                        <VIcon v-if="option.icon" dense :name="option.icon" />
                        <div class="truncate">{{ option.label }}</div>
                      </div>
                    </template>
                  </VTooltip>
                </template>
                <template v-else-if="option.type === 'separator'">
                  <hr>
                </template>
                <template v-else>
                  <VTooltip :content="option.labelTooltip ?? option.label">
                    <button
                      type="button"
                      class="px-4 pl-3 w-full text-sm leading-5 rounded-md flex gap-2 items-center whitespace-nowrap"
                      :disabled="option.disabled"
                      :class="{ 'cursor-not-allowed text-neutral-350': option.disabled, 'cursor-pointer hover:bg-primary-150 text-neutral-850': !option.disabled }"
                      @click.prevent="toggleItemsInPreset(option)"
                    >
                      <div class="py-2 truncate">{{ option.label }}</div>
                      <VIcon v-if="option.icon" class="ml-auto" dense :name="option.icon" />
                    </button>
                  </VTooltip>
                </template>
              </template>
              <template v-else>
                <VTooltip :content="option.labelTooltip ?? option.label">
                  <button
                    type="button"
                    class="px-4 pl-3 w-full text-sm leading-5 rounded-md flex gap-2 items-center whitespace-nowrap"
                    :disabled="option.disabled"
                    :class="{ 
                      'bg-primary-200': props.isSelected(option), 
                      'cursor-not-allowed text-neutral-350': option.disabled, 
                      'cursor-pointer hover:bg-primary-150': !option.disabled,
                      [theme.option.text]: !option.disabled
                    }"
                    :role="props.multiple ? 'checkbox' : 'radio'"
                    :aria-checked="props.isSelected(option)"
                    @click.prevent="select(option.value)"
                  >
                    <VCheckbox v-if="props.multiple" :model-value="props.isSelected(option)" class="pointer-events-none -ml-2" :tabindex="-1" />
                    <div class="py-2 truncate">{{ option.label ?? option.value }}</div>
                    <div class="ml-auto flex gap-2">
                      <VIcon v-if="option.icon" size="sm" :name="option.icon" />
                      <div v-if="option.badge" class="rounded-full border border-neutral-500 max-w-32 text-neutral-500 flex gap-1.5 px-2.5 py-0.5 items-center justify-center" :title="option.badge.label">
                        <VIcon v-if="option.badge.icon" size="xs" :name="option.badge.icon" />
                        <div class="truncate text-xs leading-4 font-normal">
                          {{ option.badge.label }}
                        </div>
                      </div>
                      <VButton v-for="(action, actionIndex) in option.actions" :key="actionIndex" variant="text" color="tertiary" :title="action.label" :disabled="option.disabled" @click.stop.prevent="action.action">
                        <VIcon size="sm" :name="action.icon" />
                      </VButton>
                    </div>
                  </button>
                </VTooltip>
              </template>
            </li>
          </template>
        </VLazyDisplayable>
      </ul>
    </VTeleport>
    <VSValidation v-if="validationResult" :type="validationResult[0]" :message="validationResult[1]" />
    <VSDescription v-if="props.description" :id="descriptionId" v-safe-html="props.description" />
  </div>
</template>
<script setup generic="T" lang="ts">
import { useElementId, useDescriptionElementId } from '../../../utils/utils';
import VSLabel from './VSLabel.vue';
import VSDescription from './VSDescription.vue';
import VInput from '../VInput.vue'
import VCheckbox from '../VCheckbox.vue';
import VIcon from '../../labels/VIcon.vue';
import { computed, ref, useTemplateRef } from 'vue';
import type { ValidationResult } from '@component-utils/validations';
import { getFlatCollection } from '@component-utils/search';
import { onClickOutsideOf } from '@component-utils/focus';
import { getSelectOptionsFromValues, type VSSelectBaseProps } from '../helpers/select';
import type { V } from '@component-utils/types';
import { globalLocalize } from '@component-utils/localization';
import VTeleport from '@component-library/utilities/VTeleport.vue';
import VLazyDisplayable from '@component-library/utilities/VLazyDisplayable.vue';
import { SHARED_PROPS_DEFAULTS } from '../helpers/types';
import VSValidation from './VSValidation.vue';
import { isValidationError, isValidationWarning } from '../helpers/validations';
import VSResetButton from './VSResetButton.vue';
import VTooltip from '@component-library/dialogs/VTooltip.vue';
import VButton from '@component-library/buttons/VButton.vue';
import { getDOMRect, useAnimationFramePosition } from '@component-utils/position';
import { useTheme } from '@component-library/themes/themes';
import type { UsedSelectOptionsProvider } from '../helpers/selectOptions';
import VSpinner from '@component-library/labels/VSpinner.vue';

defineOptions({
  name: 'VSSelectBase'
})

const props = withDefaults(
  defineProps<Omit<VSSelectBaseProps<T>, 'placeholder' | 'options'> & {
    /**
     * Allow multiple items to be selected at the same time
     */
    multiple?: boolean
    /**
     * Options provider
     */
    optionsProvider: UsedSelectOptionsProvider<T>
    /**
     * Options values to be always included in the available list
     */
    optionsEnsured: T[]
    /**
     * Method to check if option is currently selected
     * 
     * @param option Option to check
     */
    isSelected: (option: V.Select.Item<T>) => boolean
    /**
     * Flag to check if any option is currently selected
     */
    isAnySelected: boolean
    /**
     * Validation result
     */
    validationResult?: ValidationResult
  }>(),
  {
    ...SHARED_PROPS_DEFAULTS,
    search: false,
    actions: () => [],
    open: false,
    placeholder: undefined,
    allowEmpty: undefined,
    validationResult: undefined,
    required: false,
    theme: undefined,
    alt: undefined
  }
)

const emit = defineEmits<{
  'internal:select': [value: T],
  'internal:set': [values: T[], action: 'set' | 'select' | 'unselect']
  'internal:add': [value: T]
}>()

const slots = defineSlots<{
  default: () => unknown
  prefix?: () => unknown
  actions?: () => unknown
}>()

const menuOpen = ref(false)

const buttonElement = useTemplateRef('button')
const menuElement = useTemplateRef('menu')

onClickOutsideOf(
  [
    buttonElement,
    menuElement
  ],
  () => {
    menuOpen.value = false
  },
  {
    condition: menuOpen,
    esc: true
  }
)

const id = useElementId(props.id)
const descriptionId = useDescriptionElementId(props.description, id.value)

const validationResult = computed(() => props.validationResult)

const theme = useTheme(props, 'VSSelectBase')

const computedButtonClass = computed(() => ({
  'disabled:opacity-100 disabled:text-opacity-50': props.disabled,
  'ring-neutral-200 bg-neutral-150': theme.value.outline && props.disabled,
  'ring-0 bg-neutral-150': theme.value.outline && !props.disabled && props.readonly,
  'ring-neutral-300 bg-neutral-100': theme.value.outline && !props.disabled && !props.readonly,
  'ring-red-300': theme.value.outline && isValidationError(validationResult),
  'ring-yellow-300': theme.value.outline && isValidationWarning(validationResult),
}))

const { sort, search, allOptions, scopedOptions, next, lazy, nextLoading, findItemByValue } = props.optionsProvider

const sortingDirectionLabel = computed(() => globalLocalize(`component-library.sorting.${sort.value || 'none'}`))
const sortingDirectionIcon = computed(() => {
  switch (sort.value) {
    case 'asc': return 'Solid/chevron-up-down-top'
    case 'desc': return 'Solid/chevron-up-down-bottom'
    default: {
      return 'Solid/chevron-up-down'
    }
  }
})

const sortingDirectionToggle = () => {
  switch (sort.value) {
    case 'asc': {
      sort.value = 'desc'
      break
    }
    case 'desc': {
      sort.value = undefined
      break
    }
    default: {
      sort.value = 'asc'
      break
    }
  }
}

const optionsFlattenedRaw = computed(() => getFlatCollection(scopedOptions.value, 0, { includeNestedProperty: 'options' }))
const optionsFlattened = computed(() => {
  return [
    ...props.optionsEnsured.filter((value) => !findItemByValue(value)).map((value) => ({ value } as V.Select.Item<T> & { _nestingLevel: 0 })),
    ...optionsFlattenedRaw.value
  ]
})

const searchConfiguration = Object.assign({
  fuzzy: true,
  all: false,
  sortable: false,
  placeholder: globalLocalize('component-library.inputs.default_search_placeholder')
}, typeof props.search === 'boolean' ? {} : props.search)

const openConfiguration = Object.assign({
  trim: true,
  transform: (value: string) => value as T
}, typeof props.open === 'boolean' ? {} : props.open)

const canAdd = computed(() => {
  const value = search.value as T

  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return props.open && value && !props.optionsProvider.findItemByValue(value) && !props.optionsProvider.findItemByLabel(String(value))
})

const select = (value: T) => {
  emit('internal:select', value)

  menuOpen.value = props.multiple
}

const set = (values: T[], action: 'set' | 'select' | 'unselect') => {
  emit('internal:set', values, action)

  menuOpen.value = true
}

const add = () => {
  if (!canAdd.value) return

  emit('internal:add', openConfiguration.transform(search.value))

  menuOpen.value = props.multiple

  search.value = ''
}

const reset = () => {
  emit('internal:set', [], 'set')
}

const getItemsInGroup = (group: V.Select.ItemGroup<T>) => {
  return group.options.reduce<V.Select.Item<T>[]>((memo, option) => {
    if ('value' in option) {
      if (!option.disabled) memo.push(option)
    } else if (option.type === 'group') memo.push(...getItemsInGroup(option))
    return memo
  }, [])
}

const countSelectedItemsInGroup = (group: V.Select.ItemGroup<T>) => {
  return getItemsInGroup(group).filter(props.isSelected).length
}

const toggleItemsInGroup = (group: V.Select.ItemGroup<T>) => {
  const options = getItemsInGroup(group).map((option) => option.value)

  if (countSelectedItemsInGroup(group) === options.length) {
    set(options, 'unselect')
  } else {
    set(options, 'select')
  }
}

const toggleItemsInPreset = (option: V.Select.ItemPreset<T>) => {
  const values = getSelectOptionsFromValues(option.values, allOptions.value).filter((opt) => !opt.disabled).map((opt) => opt.value)

  set(values, 'set')
}

const position = useAnimationFramePosition(
  menuOpen,
  () => {
    const rect = getDOMRect(buttonElement.value)

    // Fixed value of 300px that needs to be clear for the dropdown to display downwards
    // Realistically it is not possible to go beyond 288px
    const menuHeight = 300
    const menuWidth = Math.max(rect.width, 250)

    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth

    const rightEdge = rect.left + menuWidth
    const leftPosition = rightEdge > windowWidth ? (rect.right - menuWidth) : rect.left

    if (rect.bottom + menuHeight > windowHeight) {
      return {
        bottom: `${windowHeight - rect.top}px`,
        left: `${leftPosition}px`,
        width: `${menuWidth}px`
      }
    } else {
      return {
        top: `${rect.bottom}px`,
        left: `${leftPosition}px`,
        width: `${menuWidth}px`
      }
    }
  }
)
</script>